import { useEffect, useRef, useState } from 'react'
import type { AskExchange } from '../api'
import { askReport, getAskHistory } from '../api'

/**
 * "Ask about this report" — the planner types the question they
 * would put to a senior planner ("can the client subdivide the duplex
 * and get two Torrens titles?") and gets an answer grounded in this
 * report and the Codes SEPP / Housing SEPP clause text. The thread is
 * kept on the report, so reopening the link shows what was asked.
 */
export function AskReport({
  reportId,
  ready,
}: {
  reportId: string
  // False while the pipeline / analyst is still running — the backend
  // refuses questions until the report is complete.
  ready: boolean
}) {
  const [history, setHistory] = useState<AskExchange[]>([])
  const [suggested, setSuggested] = useState<string[]>([])
  const [available, setAvailable] = useState<boolean | null>(null)
  const [question, setQuestion] = useState('')
  const [pending, setPending] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [elapsed, setElapsed] = useState(0)
  const threadEnd = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    let cancelled = false
    getAskHistory(reportId)
      .then((h) => {
        if (cancelled) return
        setHistory(h.history)
        setSuggested(h.suggested_questions)
        setAvailable(h.available)
      })
      .catch(() => {
        if (!cancelled) setAvailable(null)
      })
    return () => {
      cancelled = true
    }
  }, [reportId])

  // Elapsed-seconds ticker while an answer is being written — the
  // model reads the whole report and the clause text, so a good
  // answer takes 20–90 s and the planner should see it's alive.
  useEffect(() => {
    if (!pending) {
      setElapsed(0)
      return
    }
    const t0 = Date.now()
    const id = window.setInterval(() => setElapsed(Math.round((Date.now() - t0) / 1000)), 1000)
    return () => window.clearInterval(id)
  }, [pending])

  useEffect(() => {
    if (pending) threadEnd.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [pending, history.length])

  async function submit(q: string) {
    const text = q.trim()
    if (!text || pending) return
    setError(null)
    setPending(text)
    setQuestion('')
    try {
      const res = await askReport(reportId, text)
      setHistory(res.history)
      setSuggested(res.suggested_questions)
      setAvailable(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      setQuestion(text)
    } finally {
      setPending(null)
    }
  }

  const disabled = !ready || available === false || Boolean(pending)

  return (
    <section className="rounded-xl border border-massing-rule glass overflow-hidden">
      <div className="px-6 py-5">
        <div className="brand-eyebrow text-massing-ink-light mb-1">Ask about this report</div>
        <h3 className="brand-wordmark text-2xl md:text-3xl text-massing-ink leading-tight mb-2">
          Ask it the way you&apos;d ask a planner
        </h3>
        <p className="text-sm text-massing-ink-mid max-w-3xl leading-relaxed">
          Answers are drawn from this report and from the clause text of the Codes SEPP
          and the Housing SEPP. Each answer says which section and which clause it rests
          on, and what still has to be verified. It is a planning read, not legal advice.
        </p>

        {available === false && (
          <p className="mt-3 text-[13px] text-flag-amber">
            The helper isn&apos;t configured on this server (no Anthropic API key), so questions
            can&apos;t be answered here.
          </p>
        )}
        {!ready && available !== false && (
          <p className="mt-3 text-[13px] text-massing-ink-mid italic">
            Questions open once the findings analysis has finished.
          </p>
        )}

        {history.length === 0 && suggested.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {suggested.map((s) => (
              <button
                key={s}
                type="button"
                disabled={disabled}
                onClick={() => submit(s)}
                className="text-left text-[12.5px] px-3 py-1.5 rounded-md border border-massing-rule surface-shade text-massing-ink hover:border-massing disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>

      {(history.length > 0 || pending) && (
        <div className="border-t border-massing-rule-soft surface-shade-strong">
          <ul className="divide-y divide-massing-rule-soft/60">
            {history.map((ex) => (
              <li key={ex.id} className="px-6 py-4">
                <div className="brand-eyebrow text-massing-ink-light mb-1">You asked</div>
                <p className="text-[14px] font-medium text-massing-ink mb-3">{ex.question}</p>
                <div className="brand-eyebrow text-massing-ink-light mb-1">Answer</div>
                <Markdown text={ex.answer} />
                <div className="mt-2 text-[10.5px] text-massing-ink-light">
                  {formatWhen(ex.asked_at)}
                  {ex.asked_by ? ` · ${ex.asked_by}` : ''}
                </div>
              </li>
            ))}
            {pending && (
              <li className="px-6 py-4">
                <div className="brand-eyebrow text-massing-ink-light mb-1">You asked</div>
                <p className="text-[14px] font-medium text-massing-ink mb-3">{pending}</p>
                <p className="text-[13px] text-massing-ink-mid italic flex items-center gap-2">
                  <span className="inline-block w-1.5 h-1.5 rounded-full brand-gradient-bg animate-pulse" aria-hidden />
                  Reading the report and the clause text… {elapsed > 0 ? `${elapsed}s` : ''}
                </p>
              </li>
            )}
          </ul>
          <div ref={threadEnd} />
        </div>
      )}

      <form
        className="px-6 py-4 border-t border-massing-rule-soft"
        onSubmit={(e) => {
          e.preventDefault()
          void submit(question)
        }}
      >
        <label className="brand-eyebrow text-massing-ink-light block mb-1.5" htmlFor={`ask-${reportId}`}>
          Your question
        </label>
        <div className="flex gap-2 items-end">
          <textarea
            id={`ask-${reportId}`}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                void submit(question)
              }
            }}
            rows={2}
            maxLength={2000}
            disabled={disabled}
            placeholder="e.g. Can the client subdivide the duplex and get two Torrens titles?"
            className="flex-1 bg-transparent border border-massing-rule rounded-md px-3 py-2 text-[14px] text-massing-ink placeholder:text-massing-ink-light focus:outline-none focus:border-massing disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={disabled || !question.trim()}
            className="bg-massing hover:bg-massing-deep text-massing-cream px-5 py-2.5 text-sm tracking-wide transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Ask
          </button>
        </div>
        <div className="mt-1.5 text-[11px] text-massing-ink-light">
          Enter to send · Shift+Enter for a new line
        </div>
        {error && <p className="mt-2 text-[13px] text-flag-red">{error}</p>}
        {history.length > 0 && suggested.length > 0 && !pending && (
          <div className="mt-3 flex flex-wrap gap-2">
            {suggested
              .filter((s) => !history.some((h) => h.question === s))
              .slice(0, 3)
              .map((s) => (
                <button
                  key={s}
                  type="button"
                  disabled={disabled}
                  onClick={() => submit(s)}
                  className="text-left text-[12px] px-2.5 py-1 rounded-md border border-massing-rule surface-shade text-massing-ink-mid hover:text-massing-ink hover:border-massing disabled:opacity-50 transition-colors"
                >
                  {s}
                </button>
              ))}
          </div>
        )}
      </form>
    </section>
  )
}

function formatWhen(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' })
}

// ---- Minimal Markdown renderer ---------------------------------------
//
// The helper answers in a small Markdown subset (paragraphs, `-` /
// numbered bullets, `###` headings, a pipe table, **bold**, `code`).
// Rendering it here keeps the answer readable without pulling a
// Markdown library into the bundle.

function Markdown({ text }: { text: string }) {
  const blocks = parseBlocks(text)
  return (
    <div className="space-y-2 text-[13.5px] text-massing-ink leading-relaxed max-w-3xl">
      {blocks.map((b, i) => {
        switch (b.kind) {
          case 'heading':
            return (
              <div key={i} className="brand-eyebrow text-massing-ink-light pt-1">
                {renderInline(b.text)}
              </div>
            )
          case 'ul':
            return (
              <ul key={i} className="list-disc pl-5 space-y-1">
                {b.items.map((it, j) => (
                  <li key={j}>{renderInline(it)}</li>
                ))}
              </ul>
            )
          case 'ol':
            return (
              <ol key={i} className="list-decimal pl-5 space-y-1">
                {b.items.map((it, j) => (
                  <li key={j}>{renderInline(it)}</li>
                ))}
              </ol>
            )
          case 'table':
            return (
              <div key={i} className="overflow-x-auto">
                <table className="text-[12.5px] border-collapse">
                  <thead>
                    <tr>
                      {b.header.map((h, j) => (
                        <th key={j} className="text-left font-medium text-massing-ink-mid border-b border-massing-rule px-2 py-1">
                          {renderInline(h)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {b.rows.map((r, j) => (
                      <tr key={j}>
                        {r.map((c, k) => (
                          <td key={k} className="align-top border-b border-massing-rule-soft/60 px-2 py-1">
                            {renderInline(c)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          default:
            return <p key={i}>{renderInline(b.text)}</p>
        }
      })}
    </div>
  )
}

type Block =
  | { kind: 'p'; text: string }
  | { kind: 'heading'; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] }
  | { kind: 'table'; header: string[]; rows: string[][] }

function parseBlocks(text: string): Block[] {
  const lines = text.replace(/\r\n/g, '\n').split('\n')
  const blocks: Block[] = []
  let para: string[] = []
  let list: { kind: 'ul' | 'ol'; items: string[] } | null = null
  let table: string[] | null = null

  const flushPara = () => {
    if (para.length) {
      blocks.push({ kind: 'p', text: para.join(' ') })
      para = []
    }
  }
  const flushList = () => {
    if (list) {
      blocks.push(list)
      list = null
    }
  }
  const flushTable = () => {
    if (table) {
      const rows = table
        .filter((l) => !/^\|?\s*:?-{2,}/.test(l))
        .map((l) => l.replace(/^\||\|$/g, '').split('|').map((c) => c.trim()))
      if (rows.length) blocks.push({ kind: 'table', header: rows[0], rows: rows.slice(1) })
      table = null
    }
  }

  for (const raw of lines) {
    const line = raw.trimEnd()
    if (!line.trim()) {
      flushPara(); flushList(); flushTable()
      continue
    }
    if (line.trim().startsWith('|')) {
      flushPara(); flushList()
      ;(table ??= []).push(line.trim())
      continue
    }
    flushTable()
    const h = /^#{1,6}\s+(.*)$/.exec(line)
    if (h) {
      flushPara(); flushList()
      blocks.push({ kind: 'heading', text: h[1] })
      continue
    }
    const ul = /^\s*[-*•]\s+(.*)$/.exec(line)
    if (ul) {
      flushPara()
      if (!list || list.kind !== 'ul') { flushList(); list = { kind: 'ul', items: [] } }
      list.items.push(ul[1])
      continue
    }
    const ol = /^\s*\d+[.)]\s+(.*)$/.exec(line)
    if (ol) {
      flushPara()
      if (!list || list.kind !== 'ol') { flushList(); list = { kind: 'ol', items: [] } }
      list.items.push(ol[1])
      continue
    }
    if (list && /^\s{2,}\S/.test(raw)) {
      // continuation of the previous bullet
      list.items[list.items.length - 1] += ' ' + line.trim()
      continue
    }
    flushList()
    para.push(line.trim())
  }
  flushPara(); flushList(); flushTable()
  return blocks
}

function renderInline(text: string): React.ReactNode[] {
  // **bold** and `code`; everything else is text.
  const out: React.ReactNode[] = []
  const rx = /(\*\*[^*]+\*\*|`[^`]+`)/g
  let last = 0
  let m: RegExpExecArray | null
  let k = 0
  while ((m = rx.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index))
    const tok = m[0]
    if (tok.startsWith('**')) {
      out.push(<strong key={k++} className="font-semibold">{tok.slice(2, -2)}</strong>)
    } else {
      out.push(<code key={k++} className="font-mono text-[12px] px-1 rounded surface-shade">{tok.slice(1, -1)}</code>)
    }
    last = m.index + tok.length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}
